import { ConfirmModal } from '@/components/common/Modal';
import { CloseIcon, FileUploadIcon, PlusIcon, SearchIcon } from '@/components/common/icons';
import { INPUT, normalizeText } from '@/components/admin/ui';
import type { ChangeEvent, FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface EnumEditorProps {
  values: string[];
  onChange: (values: string[]) => void;
}

/** How many rows are rendered at once; code lists can hold thousands of values. */
const PAGE = 200;

const collapse = (value: string): string => value.replace(/\s+/g, ' ').trim();
const keyOf = (value: string): string => value.trim().toLowerCase();

const smallButton = (enabled: boolean): string =>
  `block rounded border bg-white px-[9px] py-[5px] text-[11.5px] font-semibold ${
    enabled ? 'cursor-pointer border-control text-muted' : 'cursor-default border-hairline text-dash'
  }`;

/**
 * The prototype's ENUM ("Číselník") editor: add one value (Enter), paste many, upload a .txt,
 * filter, spot and drop duplicates, clear, edit rows in place — 200 rows at a time.
 *
 * Controlled: `values` is the list, `onChange` the next one. Added values are trimmed, inner
 * whitespace collapsed and case-insensitive duplicates skipped; the backend cleans the same way.
 */
export const EnumEditor: FC<EnumEditorProps> = ({ values, onChange }) => {
  const { t, i18n } = useTranslation();
  const [newValue, setNewValue] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const [confirmClear, setConfirmClear] = useState(false);
  const format = (n: number) => n.toLocaleString(i18n.language);

  // Up to 50 000 values: count and filter only when the values or the query change, not on every
  // keystroke in the new-value or bulk field.
  const { counts, duplicateCount } = useMemo(() => {
    const map = new Map<string, number>();
    for (const value of values) {
      const key = keyOf(value);
      if (key) map.set(key, (map.get(key) ?? 0) + 1);
    }
    const duplicates = [...map.values()].reduce((sum, n) => sum + (n > 1 ? n - 1 : 0), 0);
    return { counts: map, duplicateCount: duplicates };
  }, [values]);

  const q = normalizeText(query.trim());
  const matching = useMemo(
    () => values.map((_, index) => index).filter((index) => !q || normalizeText(values[index]).includes(q)),
    [values, q]
  );
  const shown = matching.slice(0, limit);

  const addMany = (list: string[]) => {
    const have = new Set(values.map(keyOf));
    const added: string[] = [];
    for (const raw of list) {
      const value = collapse(raw);
      if (!value || have.has(value.toLowerCase())) continue;
      have.add(value.toLowerCase());
      added.push(value);
    }
    if (added.length) onChange([...values, ...added]);
  };

  const addNew = () => {
    if (!newValue.trim()) return;
    addMany([newValue]);
    setNewValue('');
  };

  const bulkLines = bulkText.split(/\r?\n/).map(collapse).filter(Boolean);

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      addMany(String(reader.result).split(/\r?\n/));
      input.value = '';
    };
    reader.readAsText(file);
  };

  const dedupe = () => {
    const seen = new Set<string>();
    onChange(
      values.filter((value) => {
        const key = keyOf(value);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
    );
  };

  const clear = () => {
    if (!values.length) return;
    if (q) {
      const drop = new Set(matching);
      onChange(values.filter((_, index) => !drop.has(index)));
    } else {
      setConfirmClear(true);
    }
  };

  const summary =
    t('admin.editor.values', { count: values.length, n: format(values.length) }) +
    (q ? t('admin.enum.filtered', { count: matching.length }) : '') +
    (duplicateCount ? t('admin.enum.duplicates', { count: duplicateCount }) : '');

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <input
          value={newValue}
          onChange={(event) => setNewValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addNew();
            }
          }}
          placeholder={t('admin.enum.newPlaceholder')}
          aria-label={t('admin.enum.newPlaceholder')}
          className={`${INPUT} min-w-0 flex-[1_1_220px] px-2.5 py-[7px] text-[12.5px]`}
        />
        <button
          type="button"
          onClick={addNew}
          className="flex cursor-pointer items-center gap-[5px] rounded border border-primary bg-primary px-[11px] py-[7px] text-xs font-semibold text-white hover:bg-primary-deep"
        >
          <PlusIcon size={12} strokeWidth={2.4} />
          {t('admin.enum.add')}
        </button>
        <button
          type="button"
          onClick={() => {
            setBulkOpen(!bulkOpen);
            setBulkText('');
          }}
          className={`flex cursor-pointer items-center rounded border px-[11px] py-[7px] text-xs font-semibold hover:border-primary hover:text-primary ${
            bulkOpen ? 'border-primary bg-accent-soft text-primary' : 'border-control bg-white text-muted'
          }`}
        >
          {t('admin.enum.bulk')}
        </button>
        <label className="relative flex cursor-pointer items-center gap-[5px] rounded border border-control bg-white px-[11px] py-[7px] text-xs font-semibold text-muted hover:border-primary hover:text-primary">
          <FileUploadIcon size={12} strokeWidth={2.2} />
          {t('admin.enum.upload')}
          <input
            type="file"
            accept=".txt,text/plain"
            onChange={handleFile}
            className="absolute inset-0 cursor-pointer opacity-0"
          />
        </label>
      </div>

      {bulkOpen ? (
        <div className="flex flex-col gap-1.5 border border-line bg-wash p-2.5">
          <textarea
            value={bulkText}
            onChange={(event) => setBulkText(event.target.value)}
            rows={6}
            placeholder={t('admin.enum.bulkPlaceholder')}
            aria-label={t('admin.enum.bulkPlaceholder')}
            className={`${INPUT} w-full resize-y px-2.5 py-2 text-xs leading-[1.55]`}
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex-1 text-[11.5px] text-label-2">
              {bulkLines.length ? t('admin.enum.bulkHintCount', { count: bulkLines.length }) : t('admin.enum.bulkHint')}
            </span>
            <button
              type="button"
              onClick={() => {
                setBulkOpen(false);
                setBulkText('');
              }}
              className="cursor-pointer rounded border border-control bg-white px-[11px] py-1.5 text-xs font-semibold text-muted"
            >
              {t('admin.enum.bulkCancel')}
            </button>
            <button
              type="button"
              disabled={!bulkLines.length}
              onClick={() => {
                addMany(bulkLines);
                setBulkOpen(false);
                setBulkText('');
              }}
              className={`rounded border-none px-3 py-1.5 text-xs font-semibold ${
                bulkLines.length ? 'cursor-pointer bg-primary text-white' : 'cursor-not-allowed bg-control text-faint'
              }`}
            >
              {bulkLines.length ? t('admin.enum.bulkAddCount', { count: bulkLines.length }) : t('admin.enum.bulkAdd')}
            </button>
          </div>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2.5">
        <div className="relative flex flex-[0_1_240px] items-center">
          <SearchIcon
            size={13}
            strokeWidth={2.2}
            stroke="#6d7f91"
            className="pointer-events-none absolute left-[9px]"
          />
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setLimit(PAGE);
            }}
            placeholder={t('admin.enum.filter')}
            aria-label={t('admin.enum.filter')}
            className={`${INPUT} w-full py-1.5 pl-7 pr-2.5 text-xs`}
          />
        </div>
        <span className="flex-1 text-[11.5px] text-muted">{summary}</span>
        {duplicateCount ? (
          <button type="button" onClick={dedupe} className={smallButton(true)}>
            {t('admin.enum.dedupe')}
          </button>
        ) : null}
        <button type="button" onClick={clear} className={smallButton(values.length > 0)}>
          {q ? t('admin.enum.clearFiltered') : t('admin.enum.clearAll')}
        </button>
      </div>

      <div className="min-h-[140px] flex-1 overflow-auto border border-line bg-white">
        {shown.map((index) => {
          const value = values[index];
          const duplicate = (counts.get(keyOf(value)) ?? 0) > 1;
          return (
            <div key={index} className="flex items-center gap-1.5 border-b border-[#f1f5f8] py-[3px] pl-0 pr-2">
              <span className="w-11 flex-none text-right text-[10.5px] tabular-nums text-faint">{index + 1}</span>
              <input
                value={value}
                onChange={(event) => {
                  const next = values.slice();
                  next[index] = event.target.value;
                  onChange(next);
                }}
                placeholder={t('admin.enum.value')}
                aria-label={`${t('admin.enum.value')} ${index + 1}`}
                className="min-w-0 flex-1 rounded-[3px] border px-2 py-[5px] font-[inherit] text-[12.5px] text-ink focus:border-primary focus:outline-none"
                style={{
                  background: duplicate ? '#fff8e6' : '#fff',
                  borderColor: duplicate ? '#e8c46a' : 'transparent',
                }}
              />
              {duplicate ? (
                <span className="flex-none bg-[#fbf1de] px-1.5 py-0.5 text-[10px] font-semibold text-[#7a4d00]">
                  {t('admin.enum.duplicate')}
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => onChange(values.filter((_, j) => j !== index))}
                aria-label={t('admin.enum.removeValue')}
                className="flex h-[26px] w-[26px] flex-none cursor-pointer items-center justify-center rounded border-none bg-transparent p-0 text-faint hover:bg-danger-soft hover:text-danger"
              >
                <CloseIcon size={12} strokeWidth={2.4} />
              </button>
            </div>
          );
        })}
        {shown.length === 0 ? (
          <div className="px-3.5 py-[18px] text-xs text-label-2">
            {values.length ? t('admin.enum.emptyFiltered') : t('admin.enum.empty')}
          </div>
        ) : null}
        {matching.length > shown.length ? (
          <div className="flex items-center gap-2.5 px-3.5 py-2">
            <button
              type="button"
              onClick={() => setLimit(limit + PAGE)}
              className="cursor-pointer rounded border border-control bg-white px-[11px] py-1.5 text-xs font-semibold text-primary hover:border-primary"
            >
              {t('admin.enum.more', { count: Math.min(PAGE, matching.length - shown.length) })}
            </button>
            <span className="text-[11.5px] text-label-2">
              {t('admin.enum.shown', { shown: format(shown.length), total: format(matching.length) })}
            </span>
          </div>
        ) : null}
      </div>

      {confirmClear ? (
        <ConfirmModal
          title={t('admin.enum.clearTitle')}
          text={t('admin.enum.clearText', { count: values.length })}
          confirmLabel={t('admin.enum.clearAll')}
          cancelLabel={t('admin.system.cancel')}
          onConfirm={() => {
            onChange([]);
            setConfirmClear(false);
          }}
          onCancel={() => setConfirmClear(false)}
        />
      ) : null}
    </div>
  );
};
